import React, { useState, useMemo } from 'react';
import PageHero from './PageHero';
import { Calendar, Clock, Sun, BookOpen, Star, Music, Award, Users, Globe, GraduationCap, ChevronLeft, ChevronRight, List, LayoutGrid } from 'lucide-react';

// ── Event type config ──────────────────────────────────────────────────────────
const TYPE = {
  holiday: { label: 'Public Holiday', color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500', pill: 'bg-red-500 text-white' },
  term: { label: 'Term Start/End', color: 'bg-navy/10 text-navy border-navy/20', dot: 'bg-navy', pill: 'bg-navy text-white' },
  event: { label: 'School Event', color: 'bg-amber-50 text-amber-800 border-amber-200', dot: 'bg-amber-400', pill: 'bg-amber-400 text-navy' },
  performance: { label: 'Performance/Play', color: 'bg-purple-100 text-purple-700 border-purple-200', dot: 'bg-purple-500', pill: 'bg-purple-500 text-white' },
  exhibition: { label: 'Exhibition', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', pill: 'bg-emerald-500 text-white' },
  ptm: { label: 'Parent Meet', color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500', pill: 'bg-blue-500 text-white' },
  exam: { label: 'Examination', color: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500', pill: 'bg-orange-500 text-white' },
  clubs: { label: 'Clubs / ECA', color: 'bg-teal-100 text-teal-700 border-teal-200', dot: 'bg-teal-500', pill: 'bg-teal-500 text-white' },
  graduation: { label: 'Graduation', color: 'bg-gold/20 text-[#7a5c00] border-gold/30', dot: 'bg-gold', pill: 'bg-gold text-navy' },
};

// ── All events keyed by YYYY-MM-DD (multi-day events are expanded) ─────────────
const RAW_EVENTS = [
  // June 2026
  { date: '2026-06-01', title: 'Pancasila Day', type: 'holiday' },
  { date: '2026-06-08', title: 'Summer Clubs', type: 'clubs' }, { date: '2026-06-09', title: 'Summer Clubs', type: 'clubs' }, { date: '2026-06-10', title: 'Summer Clubs', type: 'clubs' },
  { date: '2026-06-16', title: 'Islamic New Year', type: 'holiday' },
  { date: '2026-06-15', title: 'Summer Clubs', type: 'clubs' }, { date: '2026-06-17', title: 'Summer Clubs', type: 'clubs' }, { date: '2026-06-18', title: 'Summer Clubs', type: 'clubs' }, { date: '2026-06-19', title: 'Summer Clubs', type: 'clubs' },
  { date: '2026-06-22', title: 'Summer Clubs', type: 'clubs' }, { date: '2026-06-23', title: 'Summer Clubs', type: 'clubs' }, { date: '2026-06-24', title: 'Summer Clubs', type: 'clubs' }, { date: '2026-06-25', title: 'Summer Clubs', type: 'clubs' }, { date: '2026-06-26', title: 'Summer Clubs', type: 'clubs' },
  { date: '2026-06-29', title: 'Summer Clubs', type: 'clubs' }, { date: '2026-06-30', title: 'Summer Clubs', type: 'clubs' },
  // July 2026
  { date: '2026-07-13', title: 'Staff Reporting & Leadership Meet', type: 'event' },
  { date: '2026-07-14', title: 'Teachers Training', type: 'event' }, { date: '2026-07-15', title: 'Teachers Training', type: 'event' }, { date: '2026-07-16', title: 'Teachers Training', type: 'event' },
  { date: '2026-07-17', title: 'Parents Orientation', type: 'ptm' },
  { date: '2026-07-20', title: 'School Reopen – Term 1 Begins', type: 'term' },
  { date: '2026-07-21', title: 'Collaboration Planning', type: 'event' }, { date: '2026-07-22', title: 'Collaboration Planning', type: 'event' }, { date: '2026-07-23', title: 'Collaboration Planning', type: 'event' },
  { date: '2026-07-24', title: 'Students Orientation', type: 'event' },
  { date: '2026-07-27', title: 'After-school Club Starts', type: 'clubs' },
  { date: '2026-07-28', title: 'Assembly Schedule Start', type: 'event' },
  { date: '2026-07-31', title: 'After-school Club & ECAs', type: 'clubs' },
  // August 2026
  { date: '2026-08-01', title: 'University Counselling & PTA Formation', type: 'ptm' },
  { date: '2026-08-07', title: 'Investiture Ceremony (Senior)', type: 'event' },
  { date: '2026-08-12', title: 'Indonesian Week', type: 'event' }, { date: '2026-08-13', title: 'Indonesian Week', type: 'event' }, { date: '2026-08-14', title: 'Indonesian Week', type: 'event' },
  { date: '2026-08-15', title: 'Investiture (Middle & Primary)', type: 'event' },
  { date: '2026-08-17', title: 'Independence Day', type: 'holiday' },
  { date: '2026-08-22', title: 'First Parent Teacher Meet', type: 'ptm' },
  { date: '2026-08-25', title: "Prophet Muhammad's Birthday", type: 'holiday' },
  { date: '2026-08-28', title: 'Annual Athletic Meet', type: 'event' },
  // September 2026
  { date: '2026-09-05', title: 'Parental Workshop & Alumni Meet', type: 'ptm' },
  { date: '2026-09-12', title: 'Grade 12 Play', type: 'performance' },
  { date: '2026-09-19', title: 'Grade 8 Play', type: 'performance' },
  { date: '2026-09-21', title: 'Mid Term Week', type: 'exam' }, { date: '2026-09-22', title: 'Mid Term Week', type: 'exam' }, { date: '2026-09-23', title: 'Mid Term Week', type: 'exam' }, { date: '2026-09-24', title: 'Mid Term Week', type: 'exam' }, { date: '2026-09-25', title: 'Mid Term Week', type: 'exam' },
  { date: '2026-09-26', title: 'Family Sports Day', type: 'event' },
  { date: '2026-09-28', title: 'Field Trips', type: 'clubs' }, { date: '2026-09-29', title: 'Field Trips', type: 'clubs' }, { date: '2026-09-30', title: 'Field Trips', type: 'clubs' },
  // October 2026
  { date: '2026-10-02', title: 'Gandhi Day Celebration', type: 'event' },
  { date: '2026-10-03', title: 'Second PTM & Mid Report', type: 'ptm' },
  { date: '2026-10-10', title: 'Grade 5 Play & Inter-school Art', type: 'performance' },
  { date: '2026-10-11', title: 'TOK Exhibition (Grade 11)', type: 'exhibition' },
  { date: '2026-10-17', title: 'Grade 10 Play', type: 'performance' },
  { date: '2026-10-24', title: 'Grade 6 Play', type: 'performance' },
  { date: '2026-10-31', title: 'Grade 3 Play & BSEISI Meeting', type: 'performance' },
  // November 2026
  { date: '2026-11-07', title: 'Grade 4 Play', type: 'performance' },
  { date: '2026-11-11', title: 'Student Led Conference (PYP)', type: 'exhibition' },
  { date: '2026-11-14', title: 'Grade 11 Play', type: 'performance' },
  { date: '2026-11-22', title: 'Term End Examination', type: 'exam' }, { date: '2026-11-23', title: 'Term End Examination', type: 'exam' }, { date: '2026-11-24', title: 'Term End Examination', type: 'exam' }, { date: '2026-11-25', title: 'Term End Examination', type: 'exam' }, { date: '2026-11-26', title: 'Term End Examination', type: 'exam' }, { date: '2026-11-27', title: 'Term End Examination', type: 'exam' }, { date: '2026-11-28', title: 'Term End Examination', type: 'exam' },
  // December 2026
  { date: '2026-12-04', title: 'Report Card Day & Teachers Party', type: 'event' },
  { date: '2026-12-05', title: 'Parental Workshop', type: 'ptm' },
  { date: '2026-12-18', title: 'Term 1 Ends', type: 'term' },
  { date: '2026-12-25', title: 'Christmas Day', type: 'holiday' },
  // January 2027
  { date: '2027-01-01', title: "New Year's Day", type: 'holiday' },
  { date: '2027-01-05', title: "Isra Mi'raj", type: 'holiday' },
  { date: '2027-01-11', title: 'School Reopen – Term 2', type: 'term' },
  { date: '2027-01-24', title: 'GMUN', type: 'event' }, { date: '2027-01-25', title: 'GMUN', type: 'event' }, { date: '2027-01-26', title: 'GMUN', type: 'event' }, { date: '2027-01-27', title: 'GMUN', type: 'event' },
  { date: '2027-01-28', title: 'Personal Project Exhibition (Gr.10)', type: 'exhibition' },
  { date: '2027-01-30', title: 'Grade 9 Play', type: 'performance' },
  // February 2027
  { date: '2027-02-03', title: 'Grade 9 Exhibition', type: 'exhibition' },
  { date: '2027-02-04', title: 'Chinese Week', type: 'event' }, { date: '2027-02-05', title: 'Chinese Week', type: 'event' },
  { date: '2027-02-06', title: 'Chinese New Year', type: 'holiday' },
  { date: '2027-02-12', title: 'Grade 7 Play Rehearsal', type: 'performance' },
  { date: '2027-02-13', title: 'Grade 7 Play & PTM', type: 'ptm' },
  { date: '2027-02-17', title: 'Grade 8 Exhibition', type: 'exhibition' },
  { date: '2027-02-18', title: 'Visual Art Exhibition', type: 'exhibition' },
  { date: '2027-02-19', title: 'Grade 2 Play Rehearsal', type: 'performance' },
  { date: '2027-02-20', title: 'Grade 2 Play', type: 'performance' },
  // March 2027
  { date: '2027-03-02', title: 'Grade 6 Exhibition', type: 'exhibition' },
  { date: '2027-03-04', title: 'Grade 7 Exhibition', type: 'exhibition' },
  { date: '2027-03-07', title: 'Nyepi – Day of Silence', type: 'holiday' },
  { date: '2027-03-08', title: 'Eid al-Fitr Break', type: 'holiday' }, { date: '2027-03-09', title: 'Eid al-Fitr Break', type: 'holiday' }, { date: '2027-03-10', title: 'Eid al-Fitr Break', type: 'holiday' }, { date: '2027-03-11', title: 'Eid al-Fitr Break', type: 'holiday' }, { date: '2027-03-12', title: 'Eid al-Fitr Break', type: 'holiday' }, { date: '2027-03-13', title: 'Eid al-Fitr Break', type: 'holiday' },
  { date: '2027-03-16', title: 'BSEISI Examination', type: 'exam' }, { date: '2027-03-17', title: 'BSEISI Examination', type: 'exam' }, { date: '2027-03-18', title: 'BSEISI Examination', type: 'exam' }, { date: '2027-03-19', title: 'BSEISI Examination', type: 'exam' }, { date: '2027-03-20', title: 'BSEISI Examination', type: 'exam' }, { date: '2027-03-22', title: 'BSEISI Examination', type: 'exam' }, { date: '2027-03-23', title: 'BSEISI Examination', type: 'exam' }, { date: '2027-03-24', title: 'BSEISI Examination', type: 'exam' }, { date: '2027-03-25', title: 'BSEISI Examination', type: 'exam' },
  { date: '2027-03-19', title: 'Grade 1 Play Rehearsal', type: 'performance' },
  { date: '2027-03-20', title: 'Grade 1 Play', type: 'performance' },
  { date: '2027-03-26', title: 'Good Friday', type: 'holiday' },
  // April 2027
  { date: '2027-04-01', title: 'Field Trips', type: 'clubs' }, { date: '2027-04-02', title: 'Field Trips', type: 'clubs' }, { date: '2027-04-03', title: 'Field Trips', type: 'clubs' },
  { date: '2027-04-06', title: 'Grade 3 Exhibition', type: 'exhibition' },
  { date: '2027-04-08', title: 'Grade 4 Exhibition', type: 'exhibition' },
  { date: '2027-04-09', title: 'Pre-school Play Rehearsal', type: 'performance' },
  { date: '2027-04-10', title: 'Mid Term Report & Pre-school Play', type: 'ptm' },
  { date: '2027-04-11', title: 'TalkX PYP', type: 'event' },
  { date: '2027-04-12', title: 'TalkX Middle School', type: 'event' },
  { date: '2027-04-14', title: 'TEDx GMIS', type: 'event' },
  { date: '2027-04-16', title: 'Student Led Conference PYP', type: 'exhibition' },
  { date: '2027-04-17', title: 'Parent Teacher Meet', type: 'ptm' },
  { date: '2027-04-21', title: 'Kartini Day Celebration', type: 'event' },
  { date: '2027-04-22', title: 'Grade 2 Exhibition', type: 'exhibition' },
  { date: '2027-04-24', title: 'Graduation Grade 12', type: 'graduation' },
  { date: '2027-04-28', title: 'Grade 1 Exhibition', type: 'exhibition' },
  { date: '2027-04-30', title: 'IBDP-CP Exams Start', type: 'exam' },
  // May 2027
  { date: '2027-05-01', title: 'Labour Day', type: 'holiday' },
  { date: '2027-05-06', title: 'Ascension Day', type: 'holiday' },
  { date: '2027-05-08', title: 'Grade 10 Graduation', type: 'graduation' },
  { date: '2027-05-15', title: 'Parent Teacher Meet', type: 'ptm' },
  { date: '2027-05-20', title: 'Baisakhi Celebration', type: 'event' },
  { date: '2027-05-21', title: 'Term End Examination', type: 'exam' }, { date: '2027-05-22', title: 'Term End Examination', type: 'exam' }, { date: '2027-05-23', title: 'Term End Examination', type: 'exam' }, { date: '2027-05-24', title: 'Term End Examination', type: 'exam' }, { date: '2027-05-25', title: 'Term End Examination', type: 'exam' }, { date: '2027-05-26', title: 'Term End Examination', type: 'exam' }, { date: '2027-05-27', title: 'Term End Examination', type: 'exam' },
  { date: '2027-05-28', title: 'Grade 5 Graduation', type: 'graduation' }, { date: '2027-05-29', title: 'Grade 5 Graduation', type: 'graduation' },
  // June 2027
  { date: '2027-06-01', title: 'Pancasila Day', type: 'holiday' },
  { date: '2027-06-02', title: 'Prep Graduation Ceremonies', type: 'graduation' }, { date: '2027-06-03', title: 'Prep Graduation Ceremonies', type: 'graduation' }, { date: '2027-06-04', title: 'Prep Graduation Ceremonies', type: 'graduation' },
  { date: '2027-06-05', title: 'Report Card Day', type: 'event' },
  { date: '2027-06-07', title: 'Summer Break Begins', type: 'term' },
  // July 2027
  { date: '2027-07-12', title: 'Staff Reporting', type: 'event' },
  { date: '2027-07-15', title: 'Leadership Meet', type: 'event' },
  { date: '2027-07-16', title: 'Whole Teachers Meeting', type: 'event' },
  { date: '2027-07-17', title: 'Parents Orientation', type: 'ptm' },
  { date: '2027-07-19', title: 'Teachers Training', type: 'event' }, { date: '2027-07-20', title: 'Teachers Training', type: 'event' }, { date: '2027-07-21', title: 'Teachers Training', type: 'event' },
  { date: '2027-07-22', title: 'School Reopen – AY 2027-28', type: 'term' },
];

// Build lookup map
function buildEventMap(events) {
  const map = {};
  events.forEach(ev => {
    if (!map[ev.date]) map[ev.date] = [];
    map[ev.date].push(ev);
  });
  return map;
}

const EVENT_MAP = buildEventMap(RAW_EVENTS);

const MONTHS = [
  { year: 2026, month: 5, label: 'June 2026' },
  { year: 2026, month: 6, label: 'July 2026' },
  { year: 2026, month: 7, label: 'August 2026' },
  { year: 2026, month: 8, label: 'September 2026' },
  { year: 2026, month: 9, label: 'October 2026' },
  { year: 2026, month: 10, label: 'November 2026' },
  { year: 2026, month: 11, label: 'December 2026' },
  { year: 2027, month: 0, label: 'January 2027' },
  { year: 2027, month: 1, label: 'February 2027' },
  { year: 2027, month: 2, label: 'March 2027' },
  { year: 2027, month: 3, label: 'April 2027' },
  { year: 2027, month: 4, label: 'May 2027' },
  { year: 2027, month: 5, label: 'June 2027' },
  { year: 2027, month: 6, label: 'July 2027' },
];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function pad(n) { return String(n).padStart(2, '0'); }

function getCalendarCells(year, month) {
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
}

// ── EventDot ──────────────────────────────────────────────────────────────────
function EventDot({ type }) {
  return <span className={`inline-block w-2 h-2 rounded-full ${TYPE[type].dot} shrink-0`} />;
}

// ── MonthCalendar ─────────────────────────────────────────────────────────────
function MonthCalendar({ monthObj, onDateClick, selectedDate }) {
  const cells = useMemo(() => getCalendarCells(monthObj.year, monthObj.month), [monthObj]);

  return (
    <div>
      {/* Day headers */}
      <div className="grid grid-cols-7 mb-2">
        {DAYS.map(d => (
          <div key={d} className="text-center text-[11px] font-bold text-navy/40 uppercase tracking-wider py-2">{d}</div>
        ))}
      </div>
      {/* Cells */}
      <div className="grid grid-cols-7 gap-px bg-line border border-line rounded-xl overflow-hidden">
        {cells.map((day, i) => {
          if (!day) return <div key={`empty-${i}`} className="bg-white" />;
          const dateKey = `${monthObj.year}-${pad(monthObj.month + 1)}-${pad(day)}`;
          const events = EVENT_MAP[dateKey] || [];
          const isToday = new Date().toISOString().slice(0, 10) === dateKey;
          const isSelected = selectedDate === dateKey;
          const hasEvents = events.length > 0;
          const isHoliday = events.some(e => e.type === 'holiday');
          const isTerm = events.some(e => e.type === 'term');

          return (
            <div
              key={dateKey}
              onClick={() => hasEvents && onDateClick(dateKey)}
              className={`relative p-2 min-h-[80px] flex flex-col transition-all duration-200 bg-white
                ${isSelected ? 'ring-2 ring-navy ring-inset z-10 bg-navy/5' : ''}
                ${isToday && !isSelected ? 'bg-gold/5 ring-1 ring-gold ring-inset z-10' : ''}
                ${hasEvents && !isSelected ? 'hover:bg-navy/5 cursor-pointer' : ''}
                ${!hasEvents ? 'cursor-default' : ''}
              `}
            >
              <span className={`text-[13px] font-semibold leading-none mb-1.5 ${isSelected ? 'text-navy font-bold' : isHoliday ? 'text-red-500' : isTerm ? 'text-navy font-bold' : 'text-navy/80'
                }`}>
                {day}
              </span>
              {/* Event dots */}
              <div className="flex flex-wrap gap-0.5 mt-auto">
                {events.slice(0, 3).map((ev, ei) => (
                  <EventDot key={ei} type={ev.type} />
                ))}
                {events.length > 3 && (
                  <span className="text-[9px] font-bold text-navy/40">+{events.length - 3}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── EventList Sidebar ─────────────────────────────────────────────────────────
function DayDetail({ dateKey }) {
  const events = EVENT_MAP[dateKey] || [];
  if (!dateKey) return (
    <div className="h-full flex flex-col items-center justify-center text-center p-8">
      <Calendar size={40} className="text-navy/20 mb-4" />
      <p className="text-navy/40 text-sm">Click on any date with events to see details</p>
    </div>
  );

  const [y, m, d] = dateKey.split('-');
  const label = new Date(+y, +m - 1, +d).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="p-6">
      <div className="mb-5 pb-4 border-b border-line">
        <p className="text-xs text-ink/40 uppercase tracking-widest font-bold mb-1">Selected Date</p>
        <h4 className="text-lg font-heading font-bold text-navy leading-tight">{label}</h4>
        <p className="text-sm text-ink/50 mt-1">{events.length} event{events.length > 1 ? 's' : ''}</p>
      </div>
      <div className="space-y-3">
        {events.map((ev, i) => {
          const t = TYPE[ev.type];
          return (
            <div key={i} className={`rounded-xl p-4 border ${t.color}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-2 h-2 rounded-full ${t.dot} shrink-0`} />
                <span className="text-[10px] font-bold uppercase tracking-widest opacity-70">{t.label}</span>
              </div>
              <p className="font-semibold text-[15px] leading-snug">{ev.title}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function AcademicCalendarPage() {
  const [monthIdx, setMonthIdx] = useState(1); // Start at July 2026
  const [selectedDate, setSelectedDate] = useState(null);
  const [view, setView] = useState('list'); // 'calendar' | 'list'

  const monthObj = MONTHS[monthIdx];

  const stats = [
    { value: '190', label: 'School Days', icon: <BookOpen size={20} className="text-gold" /> },
    { value: '24+', label: 'Public Holidays', icon: <Sun size={20} className="text-gold" /> },
    { value: '2', label: 'Terms', icon: <Calendar size={20} className="text-gold" /> },
    { value: '6.5', label: 'Hours / Day', icon: <Clock size={20} className="text-gold" /> },
  ];

  // List view: all events for this month sorted by date
  const monthEvents = useMemo(() => {
    const entries = [];
    Object.entries(EVENT_MAP).forEach(([dateKey, evs]) => {
      const [y, m] = dateKey.split('-').map(Number);
      if (y === monthObj.year && m - 1 === monthObj.month) {
        evs.forEach(ev => entries.push({ dateKey, ...ev }));
      }
    });
    return entries.sort((a, b) => a.dateKey.localeCompare(b.dateKey));
  }, [monthObj]);

  return (
    <div className="w-full bg-[#f8f9fa] min-h-screen pb-32">

            {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <PageHero title={"Academic Calendar"} breadcrumbs={["Home","Academics","Academic Calendar"]} eyebrow={"Academic Year 2026 – 2027"} subtitle={"Stay up to date with GMIS Jakarta's official term dates, upcoming performances, exhibitions, and public holidays."} />


      {/* ── Calendar ─────────────────────────────────────────────────────────── */}
      <div className="max-w-[1100px] mx-auto px-6 -mt-4 relative z-20">

        {/* Month Quick Nav */}
        <div className="mb-4 bg-white rounded-2xl border border-line p-4 shadow-sm">
          <p className="text-[11px] font-bold text-navy/40 uppercase tracking-widest mb-3 px-1">Quick Jump</p>
          <div className="flex flex-wrap gap-2">
            {MONTHS.map((m, i) => (
              <button
                key={i}
                onClick={() => { setMonthIdx(i); setSelectedDate(null); }}
                className={`text-[12px] font-semibold px-3 py-1.5 rounded-lg transition-all ${monthIdx === i ? 'bg-navy text-white' : 'bg-[#f0f2f5] text-navy/60 hover:text-navy hover:bg-navy/10'}`}
              >
                {m.label.split(' ')[0]} {m.label.split(' ')[1].slice(2)}
              </button>
            ))}
          </div>
        </div>

        {/* Month Nav + View Toggle */}
        <div className="bg-white rounded-2xl shadow-xl border border-line px-6 py-4 flex items-center justify-between mb-4">
          <button
            onClick={() => { setMonthIdx(i => Math.max(0, i - 1)); setSelectedDate(null); }}
            disabled={monthIdx === 0}
            className="w-10 h-10 rounded-full bg-[#f0f2f5] hover:bg-navy hover:text-white text-navy flex items-center justify-center transition-all disabled:opacity-30"
          >
            <ChevronLeft size={18} />
          </button>

          <div className="text-center">
            <h2 className="text-2xl font-heading font-bold text-navy">{monthObj.label}</h2>
            <p className="text-xs text-ink/40 mt-0.5">
              {Object.entries(EVENT_MAP).filter(([k]) => k.startsWith(`${monthObj.year}-${pad(monthObj.month + 1)}`)).length} events this month
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex bg-[#f0f2f5] rounded-xl p-1 gap-1">
              <button
                onClick={() => setView('calendar')}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${view === 'calendar' ? 'bg-navy text-white shadow' : 'text-navy/50 hover:text-navy'}`}
              >
                <LayoutGrid size={15} /> Calendar
              </button>
              <button
                onClick={() => setView('list')}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${view === 'list' ? 'bg-navy text-white shadow' : 'text-navy/50 hover:text-navy'}`}
              >
                <List size={15} /> List
              </button>
            </div>
            <button
              onClick={() => { setMonthIdx(i => Math.min(MONTHS.length - 1, i + 1)); setSelectedDate(null); }}
              disabled={monthIdx === MONTHS.length - 1}
              className="w-10 h-10 rounded-full bg-[#f0f2f5] hover:bg-navy hover:text-white text-navy flex items-center justify-center transition-all disabled:opacity-30"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Calendar Grid / List View */}
        {view === 'calendar' ? (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
            {/* Calendar */}
            <div className="bg-white rounded-2xl shadow-sm border border-line p-6">
              <MonthCalendar monthObj={monthObj} onDateClick={setSelectedDate} selectedDate={selectedDate} />
            </div>
            {/* Day Detail Sidebar */}
            <div className="bg-white rounded-2xl shadow-sm border border-line overflow-hidden min-h-[300px]">
              <DayDetail dateKey={selectedDate} />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
            <div className="bg-white rounded-2xl shadow-sm border border-line p-6">
              {monthEvents.length === 0 ? (
                <div className="text-center py-16 text-navy/30">
                  <Calendar size={40} className="mx-auto mb-4 opacity-30" />
                  <p>No events this month</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {monthEvents.map((ev, i) => {
                    const t = TYPE[ev.type];
                    const [y, m, d] = ev.dateKey.split('-').map(Number);
                    const dayLabel = new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
                    return (
                      <div key={i} className={`flex items-center gap-4 rounded-xl px-5 py-3 border ${t.color} hover:shadow-sm transition-shadow`}>
                        <div className="w-14 text-[12px] font-bold shrink-0 opacity-70">{dayLabel}</div>
                        <div className={`w-2 h-2 rounded-full ${t.dot} shrink-0`} />
                        <div className="flex-1 font-semibold text-[14px]">{ev.title}</div>
                        <span className="text-[10px] font-bold uppercase tracking-wider opacity-60 hidden md:block">{t.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="flex flex-col gap-4 relative">
              <div className="bg-white rounded-2xl border border-line p-5 shadow-sm lg:sticky lg:top-[160px]">
                <p className="text-[11px] font-bold text-navy/40 uppercase tracking-widest mb-3">Legend</p>
                <div className="flex flex-col gap-2">
                  {Object.entries(TYPE).map(([key, t]) => (
                    <span key={key} className={`inline-flex items-center gap-2 text-[11px] font-bold tracking-wide uppercase px-3 py-2 rounded-lg border ${t.color}`}>
                      <span className={`w-2 h-2 rounded-full ${t.dot}`} /> {t.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Term Dates & Notes ───────────────────────────────────────────────── */}
      <div className="max-w-[1100px] mx-auto px-6 mt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-line p-8 shadow-sm">
            <h3 className="text-lg font-heading font-bold text-navy mb-5 flex items-center gap-3">
              <Calendar size={18} className="text-gold" /> Term Dates
            </h3>
            {[
              ['Term 1 Start', '20 July 2026'],
              ['Term 1 End', '18 December 2026'],
              ['Term 2 Start', '11 January 2027'],
              ['Term 2 End', '4 June 2027'],
            ].map(([l, v], i) => (
              <div key={i} className="flex justify-between py-3 border-b border-line last:border-0">
                <span className="text-ink/60 text-sm">{l}</span>
                <span className="font-bold text-navy text-sm">{v}</span>
              </div>
            ))}
            <p className="text-ink/40 text-xs mt-3">Total ~190 instructional days</p>
          </div>
          <div className="bg-[#fef8f0] border border-gold/30 rounded-2xl p-8 shadow-sm">
            <h3 className="text-lg font-heading font-bold text-navy mb-5 flex items-center gap-3">
              <Star size={18} className="text-gold" /> General Notes
            </h3>
            {[
              'All dates subject to change — official channels will communicate updates.',
              'Parent-Teacher Meets require prior appointments (except Mondays).',
              'Islamic holidays based on moon sighting and may shift by one day.',
              'Inter/intra school competition dates will be announced separately.',
            ].map((n, i) => (
              <div key={i} className="flex items-start gap-2.5 mb-3 last:mb-0">
                <div className="w-4 h-4 rounded-full bg-gold/30 flex items-center justify-center shrink-0 mt-0.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-gold" />
                </div>
                <p className="text-ink/75 text-[13px] leading-relaxed">{n}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
